import { atom, atomFamily, selector } from 'recoil';
import moment from 'moment'

export const isFromSearchState = atom({
    key: 'isFromSearchState',
    default: false
})

export const stompClientState = atom({
    key: 'stompClientState',
    default: null,
    dangerouslyAllowMutability: true 
})

export const playerBedDataState = atom({
    key: 'playerBedDataState',
    default: {}
})

export const alarmReviewSelectedTimeState = atom({
    key: 'alarmReviewSelectedTimeState',
    default: null
})

export const unitState = atom({
    key: 'unitState',
    default: {
        'HR/PR': 'bpm',
        'PR': 'bpm',
        'HR': 'bpm',
        'RR': 'rpm',
        'RESP': 'rpm',
        'SpO2': '%',
        'NIBP': 'mmHg',
        'IBP': 'mmHg',
        'IBP1': 'mmHg',
        'IBP2': 'mmHg',
        'IBP3': 'mmHg',
        'IBP4': 'mmHg',
        'EtCO2': 'mmHg',
        'InCO2': 'mmHg',
        'Temp': '°C',
        'Temp1': '°C',
        'Temp2': '°C',
        'IPI': '',
        'EWS': 'total',
        'awRR': 'rpm',
        'GAS': 'vol%'
    }
})

export const monitorState = atom({
    key: 'monitorState',
    default: 1
})

export const wardState = atom({
    key: 'wardState',
    default: { value: 1, label: '중환자실' }
})

export const bedCountState = atom({
    key: 'bedCountState',
    default: [8, 8]
});

export const selectBedInfoState = atom({
    key: 'selectBedInfoState',
    default: null
})

export const bedSocketState = atom({
    key: 'bedSocketState',
    default: []
});

export const bedInfoState = atom({
    key: 'bedInfoState',
    default: [],
});

export const value1State = atom({
    key: 'value1State',
    default: [],
});

export const value2State = atom({
    key: 'value2State',
    default: [],
});

export const subMonitorValue1State = atom({
    key: 'subMonitorValue1State',
    default: [],
});

export const subMonitorValue2State = atom({
    key: 'subMonitorValue2State',
    default: [],
});

export const connectedBedsState = atom({
    key: 'connectedBedsState',
    default: [],
});

export const trendZoomState = atom({
    key: 'trendZoomState',
    default: '15m'
});

export const alarmsState = atom({
    key: 'alarmsState',
    default: {}
})

export const alarmStateFamily = atomFamily({
    key: 'alarmStateFamily',
    default: (serial) => ({
        serial: serial,
        alarmActive: true,
        alarmMessage: '',
        alarmCode: '',
        alarmPriority: '',
        alarmLimit: '',
        alarmMode: '',
    }),
});

export const measurementStateFamily = atomFamily({
    key: 'measurementStateFamily',
    default: (serial) => ({
        serial: serial,
        lead1SweepSpeed: 25,
        lead2SweepSpeed: 25,
        lead3SweepSpeed: 25,
        plethSweepSpeed: 25,
        respSweepSpeed: 25,
        capnoSweepSpeed: 25,
        ibp1SweepSpeed: 25,
        ibp2SweepSpeed: 25,
        ibp3SweepSpeed: 25,
        ibp4SweepSpeed: 25,
        gasAa1SweepSpeed: 25,
        gasAa2SweepSpeed: 25,
        gasCo2SweepSpeed: 25,
        gasN2oSweepSpeed: 25,
        gasO2SweepSpeed: 25,
        chestSweepSpeed: 25,
        padsPaddleSweepSpeed: 25,
        intervalSeconds: 0,
    }), 
})

export const dataQueueStateFamily = atomFamily({
    key: 'dataQueueStateFamily',
    default: ({serial, type}) => ({
        serial: serial,
        type: type,
        data: []
    })
})

export const lastRespValueStateFamily = atomFamily({
    key: 'lastRespValueState',
    default: null,
});

//알람 오디오 상태 (임시)
export const audioPlayState = atom({
    key: 'audioPlayState',
    default: false,
});

export const wardSuspendState = atom({
    key: 'wardSuspendState',
    default: '0',  // 0=On, 1=Off, 2=Silence
});

export const wardSilenceState = atom({
    key: 'wardSilenceState',
    default: {
        isSilenced: false,      // 병동 Silence on/off
        startTime: null,        // 병동 Silence 시작 시각(Date.now())
        remainingSeconds: null, // 남은 초 (120초에서 감소)
    },
});

export const silenceStateFamily = atomFamily({
    key: 'silenceStateFamily',
    default: () => ({
        isSilenced: false,      // Silence on/off
        silenceStartTime: null, // Silence 시작 시각(Date.now())
        duration: 120000,       // Silence 시간 (120초)
        remainingSeconds: null, // 남은 초 (120초에서 감소)
    })
});

export const acknowledgeStateFamily = atomFamily({
    key: 'acknowledgeStateFamily',
    default: {
        isAcknowledged: false,
        acknowledgeStartTime: null,
        duration: 120000, // 2분
    },
});

export const globalAlarmState = atom({
    key: 'globalAlarmState',
    default: {
        alarms: [], // 현재 활성 알람 목록
        isPlaying: false, // 알람 재생 상태
        highestPriority: null, // 가장 높은 우선순위 알람
        alarmStartTime: null, // 타이머 시작 시간
        alarmRemaining: 0, // 남은 타이머 시간
    },
});

//#region 
//기본 Wave 파라미터 색상 설정
export const defaultWaveParameterColors = {
    "LEADⅠ": "#3DFF3D",
    "LEADⅡ": "#3DFF3D",
    "LEADⅢ": "#3DFF3D",
    "PLETH": "#09F4F0",
    "RESP": "#FDE401",
    "CAPNO": "#FFFFFF",
    "IBP1": "#FF2B5E",
    "IBP2": "#FDE401",
    "IBP3": "#FF2B5E",
    "IBP4": "#FDE401",
    "GasCO2": "#FF8F32",
    "GasAA1": "#FF8F32",
    "GasAA2": "#FF8F32",
    "GasN2O": "#FF8F32",
    "GasO2": "#FF8F32",
    "CHEST": "#3DFF3D",
    "Pads/Paddle": "#3DFF3D"
}

//기본 Numeric 파라미터 색상 설정
export const defaultNumericParameterColors = {
    "HR": "#3DFF3D",
    "PR": "#3DFF3D",
    "SpO2": "#09F4F0",
    "RESP": "#FDE401",
    "awRR": "#FFD3BD",
    "NIBP": "#FF2B5E",
    "IBP1": "#FF2B5E",
    "IBP2": "#FDE401",
    "IBP3": "#FF2B5E",
    "IBP4": "#FDE401",
    "Temp1": "#9B92FF",
    "Temp2": "#9B92FF",
    "GAS": "#FF8F32",
    "EtCO2": "#FFFFFF",
    "InCO2": "#FFFFFF",
    "IPI": "#FFFFFF",
    "BIS": "#FFFFFF",
    "EWS": "#FFFFFF",
}
//#endregion

//Wave와 Numeric 파라미터 색상 설정 
export const globalParameterColorState = atom({
    key: 'globalParameterColorState',
    default: {        
        waveParameterColors: defaultWaveParameterColors,
        numericParameterColors : defaultNumericParameterColors
    },
});

// 
export const globalConnectedDeviceState = atom({
    key: 'globalConnectedDeviceState',
    default: {        
        devices : []
    },
});

// Device 동기화 타임 세팅
export const timeState = atom({
    key: 'timeState',
    default: moment()
});

// Demo Mode State
export const demoModeState = atom({
    key: 'demoModeState',
    default: false
});

// device model info
export const deviceModelInfo = atom({
    key: 'deviceModelInfo',
    default: []
});

// v20 model data save
export const v20DatasState = atom({
    key: 'v20DatasState',
    default: {}
})

// hl7 status data save
export const hl7InfoStatus = atom({
    key: 'hl7InfoStatus',
    default: {}
})
